import React, { useEffect, useState } from "react";
import usersApi from "../services/users/UserApi";
import { Flowbite, Modal } from "flowbite-react";
import CommonHeader from "../components/CommonHeader";
import ToastNotification, { showToast } from "../components/Toaster";
import PillButton from "../components/button/Pills";

const SubscriptionsPage: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(
    null
  );
  const [newEndDate, setNewEndDate] = useState<string>("");
  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getSubscriptions();
      if (response.success) {
        setSubscriptions(response.value.data.contents);
      } else {
        setError(response.error.message || "Failed to fetch subscriptions.");
      }
    } catch (err) {
      setError("Failed to fetch subscriptions.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handleUpdateSubscription = async () => {
    if (selectedSubscription && newEndDate) {
      try {
        const endDate = new Date(newEndDate).getTime();
        const response = await usersApi.updateSubscription({
          userId: selectedSubscription.user.id,
          endDate,
        });
        if (response.success) {
          fetchSubscriptions();
          showToast("Subscription updated successfully!", "success");
          setSubscriptions((prev) =>
            prev.map((sub) =>
              sub.user.id === selectedSubscription.user.id
                ? { ...sub, endDate }
                : sub
            )
          );
          setOpenUpdateModal(false);
        } else {
          showToast("Failed to update subscription.", "error");
        }
      } catch (err) {
        showToast("Failed to update subscription.", "error");
      }
    }
  };

  return (
    <Flowbite>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader title="Subscriptions" />
          {loading ? (
            <p className="text-gray-500 text-center dark:text-gray-50 mt-[200px]">
              Loading...
            </p>
          ) : error ? (
            <p className="text-red-500 text-center">{error}</p>
          ) : subscriptions && subscriptions.length === 0 ? (
            <p className="text-gray-500 text-center">No records found.</p>
          ) : (
            <div className="w-full p-16">
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-900 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Username
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Plan Type
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Start Date
                      </th>
                      <th scope="col" className="px-6 py-3">
                        End Date
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Remaining Days
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr
                        key={sub.id}
                        className="bg-white border-b dark:bg-gray-700 dark:border-gray-700"
                      >
                        <td className="px-6 py-4 dark:text-gray-50">
                          {sub.user.username}
                        </td>
                        <td className="px-6 py-4 dark:text-gray-50">
                          {sub.planType}
                        </td>
                        <td className="px-6 py-4 dark:text-gray-50">
                          {new Date(sub.startDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 dark:text-gray-50">
                          {new Date(sub.endDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 dark:text-gray-50 text-left">
                          {sub.remainingDays}
                        </td>
                        <td className="px-6 py-4 dark:text-gray-50">
                          <button
                            className="text-gray-500 dark:text-gray-100 underline"
                            onClick={() => {
                              setSelectedSubscription(sub);
                              setNewEndDate(
                                new Date(sub.endDate)
                                  .toISOString()
                                  .split("T")[0]
                              );
                              setOpenUpdateModal(true);
                            }}
                          >
                            <svg
                              stroke="currentColor"
                              fill="none"
                              stroke-width="2"
                              viewBox="0 0 24 24"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              height="15px"
                              width="15px"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={openUpdateModal}
        size="md"
        onClose={() => setOpenUpdateModal(false)}
        popup
      >
        <Modal.Header className="dark:bg-gray-900 bg-gray-200 rounded-t-lg">
          <h3 className="text-lg font-bold">Update Subscription</h3>
        </Modal.Header>
        <Modal.Body className="dark:bg-gray-600 bg-white rounded-b-lg">
          <div className="space-y-4 ">
            <p className="text-gray-700 dark:text-gray-50">
              Update subscription for:{" "}
              <strong>{selectedSubscription?.user.username}</strong>
            </p>
            <input
              type="date"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              min={
                selectedSubscription?.startDate
                  ? new Date(selectedSubscription.startDate).toISOString().split("T")[0]
                  : undefined
              }
              className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-100"
            />
            <div className="flex justify-end gap-4">
              <PillButton variant="primary" size="md" onClick={handleUpdateSubscription}>
              
              
                Save
              </PillButton>
              <PillButton onClick={() => setOpenUpdateModal(false)}>
                Cancel
              </PillButton>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Flowbite>
  );
};

export default SubscriptionsPage;
